import type { FinancialSummary } from '@/hooks/use-financial-summary';
import type { Charge } from '@/types/charge';
import { CHARGE_STATUS } from '@/types/charge-status';
import { BILLING_MODEL } from '@/types/billing-model';
import { ENROLLMENT_STATUS } from '@/types/enrollment-status';
import { PROCESSING_METHOD } from '@/types/processing-method';
import { PROVIDER_STATUS } from '@/types/provider-status';
import type { Provider } from '@/types/provider';
import { RECURRENCE_INTERVAL } from '@/types/recurrence-interval';
import type { Enrollments } from '@/types/enrollments';
import type { OperationalMetrics } from '@/types/operational-metrics';
import type { Service } from '@/types/service';
import { SERVICE_FREQUENCY } from '@/types/service-frequency';
import { USER_ROLE } from '@/types/user-role';
import { format, endOfMonth, startOfMonth } from 'date-fns';

const now = new Date();

function daysFromNow(days: number): Date {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date;
}

function makePaginated<T>(items: T[], page = 1, limit = 10) {
  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  return {
    data: items.slice(0, limit),
    meta: {
      page,
      limit,
      total,
      totalPages,
      hasNextPage: page < totalPages,
      hasPreviousPage: page > 1,
    },
  };
}

function makeClient(name: string, slug: string) {
  return {
    id: `mock-client-${slug}`,
    phone: '11999990000',
    createdAt: now,
    updatedAt: now,
    enrollments: [] as Enrollments[],
    user: {
      id: `mock-user-${slug}`,
      email: `${slug}@exemplo.com`,
      username: name,
      role: USER_ROLE.CLIENT,
      createdAt: now,
      updatedAt: now,
      isOnboarded: true,
    },
  };
}

function makeCharge(
  id: string,
  enrollmentId: string,
  amount: number,
  status: CHARGE_STATUS,
  dueDate: Date,
): Charge {
  return {
    id,
    amount,
    dueDate,
    status,
    createdAt: now,
    updatedAt: now,
    enrollmentId,
    processingMethod: PROCESSING_METHOD.MANUAL,
    paidAt: status === CHARGE_STATUS.PAID ? daysFromNow(-2) : undefined,
  };
}

function makeEnrollmentStub(id: string): Enrollments {
  return {
    id,
    price: 0,
    startDate: now,
    status: ENROLLMENT_STATUS.ACTIVE,
    createdAt: now,
    updatedAt: now,
    processingMethod: PROCESSING_METHOD.MANUAL,
    chargeSchedule: {
      id: `mock-schedule-${id}`,
      billingModel: BILLING_MODEL.RECURRING,
      recurrenceInterval: RECURRENCE_INTERVAL.MONTHLY,
      chargeDay: 10,
      createdAt: now,
      updatedAt: now,
    },
    chargeExceptions: [],
    charges: [],
  };
}

function makeService(
  id: string,
  name: string,
  defaultPrice: number,
  contractCount: number,
): Service {
  return {
    id,
    name,
    description: name,
    defaultPrice,
    isActive: true,
    address: 'São Paulo, SP',
    allowedPaymentMethods: [],
    createdAt: now,
    enrollments: Array.from({ length: contractCount }, (_, index) =>
      makeEnrollmentStub(`mock-enr-${id}-${index}`),
    ),
  };
}

// Um ramo só (personal trainer), com os serviços e preços do pacote do ramo
// (`api/src/activity-pack/packs/fitness.packs.ts`, slug `personal-trainer`).
const MOCK_SERVICES: Service[] = [
  makeService('mock-svc-plano-2x', 'Plano 2x por semana', 520, 4),
  makeService('mock-svc-plano-3x', 'Plano 3x por semana', 700, 3),
  makeService('mock-svc-avaliacao', 'Avaliação física', 130, 2),
  makeService('mock-svc-avulsa', 'Aula avulsa', 100, 1),
  makeService('mock-svc-online', 'Treino online', 200, 2),
];

function makeEnrollment(
  id: string,
  clientName: string,
  clientSlug: string,
  service: Service,
  price: number,
  charges: Charge[],
  serviceFrequency: SERVICE_FREQUENCY = SERVICE_FREQUENCY.WEEKLY,
): Enrollments {
  return {
    id,
    price,
    startDate: daysFromNow(-45),
    status: ENROLLMENT_STATUS.ACTIVE,
    createdAt: now,
    updatedAt: now,
    processingMethod: PROCESSING_METHOD.MANUAL,
    chargeSchedule: {
      id: `mock-charge-schedule-${id}`,
      billingModel: BILLING_MODEL.RECURRING,
      recurrenceInterval: RECURRENCE_INTERVAL.MONTHLY,
      chargeDay: 10,
      createdAt: now,
      updatedAt: now,
    },
    chargeExceptions: [],
    charges,
    client: makeClient(clientName, clientSlug),
    service: {
      id: service.id,
      name: service.name,
      description: service.description,
      isActive: service.isActive,
      address: service.address,
      allowedPaymentMethods: service.allowedPaymentMethods,
      createdAt: service.createdAt,
      enrollments: [],
    },
    serviceSchedules: [
      {
        id: `mock-service-schedule-${id}`,
        frequency: serviceFrequency,
        daysOfWeek: serviceFrequency === SERVICE_FREQUENCY.WEEKLY ? [1, 3, 5] : undefined,
        dayOfMonth: serviceFrequency === SERVICE_FREQUENCY.MONTHLY ? 10 : undefined,
        startTime: '08:00',
        endTime: '09:00',
        createdAt: now,
        updatedAt: now,
      },
    ],
  };
}

const MOCK_ENROLLMENTS: Enrollments[] = [
  makeEnrollment(
    'mock-enr-1',
    'Maria Oliveira',
    'maria',
    MOCK_SERVICES[0],
    520,
    [makeCharge('mock-charge-1', 'mock-enr-1', 520, CHARGE_STATUS.PAID, daysFromNow(-5))],
    SERVICE_FREQUENCY.WEEKLY,
  ),
  makeEnrollment(
    'mock-enr-2',
    'João Pedro Santos',
    'joao',
    MOCK_SERVICES[1],
    700,
    [
      makeCharge('mock-charge-2', 'mock-enr-2', 700, CHARGE_STATUS.PENDING, daysFromNow(4)),
    ],
    SERVICE_FREQUENCY.MONTHLY,
  ),
  makeEnrollment(
    'mock-enr-3',
    'Carla Mendes',
    'carla',
    MOCK_SERVICES[2],
    130,
    [
      makeCharge('mock-charge-3', 'mock-enr-3', 130, CHARGE_STATUS.OVERDUE, daysFromNow(-6)),
    ],
    SERVICE_FREQUENCY.WEEKLY,
  ),
  makeEnrollment(
    'mock-enr-4',
    'Ricardo Teixeira',
    'ricardo',
    MOCK_SERVICES[3],
    100,
    [makeCharge('mock-charge-4', 'mock-enr-4', 100, CHARGE_STATUS.PAID, daysFromNow(-1))],
    SERVICE_FREQUENCY.MONTHLY,
  ),
  makeEnrollment(
    'mock-enr-5',
    'Patrícia Nunes',
    'patricia',
    MOCK_SERVICES[4],
    200,
    [
      makeCharge('mock-charge-5', 'mock-enr-5', 200, CHARGE_STATUS.PENDING, daysFromNow(8)),
    ],
    SERVICE_FREQUENCY.WEEKLY,
  ),
];

export const MARKETING_HOME_OPERATIONAL_METRICS: OperationalMetrics = {
  monthlyRevenue: 4850,
  activeEnrollments: 12,
  clientMetrics: {
    total: 8,
    newThisMonth: 3,
  },
  weeklyGrowth: 12,
};

export const MARKETING_HOME_FINANCIAL_SUMMARY: FinancialSummary = {
  balance: {
    available: 3250,
    pending: 2340,
  },
  recentPayouts: [],
};

export const MARKETING_HOME_PROVIDER: Provider = {
  id: 'marketing-mock-provider',
  title: 'Lucas Martins Personal',
  bio: 'Personal trainer em São Paulo: treino na academia, em casa ou online.',
  businessPhone: '11987654321',
  status: PROVIDER_STATUS.ACTIVE,
  address: 'São Paulo, SP',
  createdAt: now,
  updatedAt: now,
  subscriptionId: 'sub_marketing_mock',
  subscriptionStatus: 'active',
  subscriptionPlanId: 'professional',
  trialEndsAt: null,
  services: MOCK_SERVICES,
  onboardingCompleted: true,
};

export const MARKETING_HOME_CHARGES: Charge[] = MOCK_ENROLLMENTS.flatMap(
  enrollment => enrollment.charges,
);

export const MARKETING_HOME_EXPENSES_TOTAL = 1840;

type EnrollmentsFilters = { page?: number; limit?: number } | undefined;

type ServicesFilters =
  | {
      q?: string;
      isActive?: boolean;
      page?: number;
      limit?: number;
    }
  | undefined;

export function getMarketingHomeEnrollments(filters?: EnrollmentsFilters) {
  const page = filters?.page ?? 1;
  const limit = filters?.limit ?? 10;
  return makePaginated(MOCK_ENROLLMENTS, page, limit);
}

export function getMarketingHomeServices(filters?: ServicesFilters) {
  const page = filters?.page ?? 1;
  const limit = filters?.limit ?? 10;
  let list = [...MOCK_SERVICES];

  if (filters?.isActive === true) {
    list = list.filter(service => service.isActive);
  } else if (filters?.isActive === false) {
    list = list.filter(service => !service.isActive);
  }

  const query = filters?.q?.trim().toLowerCase();
  if (query) {
    list = list.filter(
      service =>
        service.name.toLowerCase().includes(query) ||
        service.description.toLowerCase().includes(query),
    );
  }

  return makePaginated(list, page, limit);
}

export function getMarketingHomeExpensesTotalKeyDates() {
  const nowDate = new Date();
  return {
    startDate: format(startOfMonth(nowDate), 'yyyy-MM-dd'),
    endDate: format(endOfMonth(nowDate), 'yyyy-MM-dd'),
  };
}
